import React from 'react';
import styled from 'styled-components';
import { Body5, Container } from '@ui';
import { CollapseButton } from '@ui/buttons/CollapseButton';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import { useConfirmationModal } from '@hooks/application/useConfirmationModal';
import { sendApiRequest } from '@services/api';
import { logNetRequest, sleep } from '@utils/usefulFuncs';
import { showErrorBanner, showSuccessBanner } from '@utils/application';
import { useDispatch } from 'react-redux';

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;
const RFPItemWrapper = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;

  &.is-collapsed {
  cursor: pointer;
`;
const RFPItemDescription = styled.div`
  height: 40px;

  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const ShowFieldsActionsWrapper = styled.div`
  padding: 24px 0 0 0;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  button {
    padding: 8px 0;
  }
`;

const RFPItemBody = styled.div``;
const FieldWrapper = styled.div`
  min-height: 48px;
  width: 100%;

  textarea,
  input {
    display: inline-block;
    font-size: 14px;
    border: none;
  }

  input {
    width: auto;
    min-width: unset;
    max-width: unset;
    height: 40px;
  }
  textarea {
    padding-top: 18px;
    width: 100%;
    max-height: 820px;
    resize: vertical;
  }

  button {
    padding: 8px 16px;
  }
`;
const InlineColumn = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: flex-start;
  gap: 8px;

  input {
    width: 100%;
  }
`;

const isBlank = (value) => {
  return value === null || value === undefined || value === '';
};

const stripCannotUpdateName = (name) => {
  return name.replace('(Cannot update)', '').trim();
};

const isCannotUpdateName = (name) => {
  return false; //disabled
  //   return name.includes('(Cannot update)');
};

const replaceUnderscores = (name) => {
  return name.replace(/_/g, ' ');
};

const formatFieldName = (name) => {
  return replaceUnderscores(stripCannotUpdateName(name));
};

const getFieldData = (name, fieldData) => {
  return fieldData.find((field) => field.name === name);
};

const textAreaFieldNames = ['Answer_Response', 'Answer_Response 1', 'Answer_Response 2', 'Answer_Response 3'];

const SaveButton = ({ isSaving, onClick }) => {
  if (isSaving) {
    return (
      <FlatButton fullWidth disabled>
        Saving...
      </FlatButton>
    );
  }
  return (
    <FlatButton fullWidth onClick={onClick}>
      Save
    </FlatButton>
  );
};

const Field = ({ data, onFieldChange, onSave }) => {
  const textareaRef = React.useRef(null);
  const [didInitTextAreaSizing, setDidInitTextAreaSizing] = React.useState(false);

  const { name, value } = data;

  React.useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea && !didInitTextAreaSizing) {
      const approxHeight = textarea.scrollHeight;
      textarea.style.height = 'auto';
      textarea.style.height = approxHeight + 'px';
      setDidInitTextAreaSizing(true);
    }
  }, [value, didInitTextAreaSizing]);

  const handleFieldChange = (e) => {
    onFieldChange(name, e.target.value);
  };

  if (textAreaFieldNames.includes(name)) {
    return (
      <FieldWrapper>
        <Container bottom={10}>
          <InlineColumn>
            <Container top={17}>
              <Body5 bold>{formatFieldName(name)}: </Body5>
            </Container>
            <Container bottom={6}>
              <textarea ref={textareaRef} value={value} placeholder="Empty" onChange={handleFieldChange} />
            </Container>
          </InlineColumn>
        </Container>
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper>
      <InlineColumn>
        <Container top={11}>
          <Body5 bold>{formatFieldName(name)}: </Body5>
        </Container>
        <Container>
          <input type="text" value={value} placeholder="Empty" onChange={handleFieldChange} />
        </Container>
      </InlineColumn>
    </FieldWrapper>
  );
};
export const RFPField = Field;

const NoEditField = ({ data }) => {
  const { name, value } = data;

  return (
    <FieldWrapper>
      <InlineColumn>
        <Body5 bold>{stripCannotUpdateName(name)}: </Body5>
        <input type="text" value={value} placeholder="Empty" onChange={() => null} readOnly />
      </InlineColumn>
    </FieldWrapper>
  );
};

const updateFieldData = (fieldData, name, value) => {
  const copy = fieldData.map((field) => ({ ...field }));
  const index = copy.findIndex((field) => field.name === name);
  if (index !== -1) {
    copy[index].value = value;
  }
  return copy;
};

const mainFieldNames = ['Question', 'Answer_Response'];
const noUseFieldNames = ['Attached Files (Cannot update)', 'Has Image (Cannot update)'];

export const RFPItem = ({ data, onSave }) => {
  const dispatch = useDispatch();

  const { id, doc_metadata, fields } = data;
  const { filename, doc_type } = doc_metadata || {};

  const [isSaving, setIsSaving] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);

  const [fieldData, setFieldData] = React.useState([]);

  const [mainDisplayFieldNames] = React.useState(mainFieldNames);
  const [displayFieldNames, setDisplayFieldNames] = React.useState([]);
  const [cannotUpdateFieldNames, setCannotUpdateFieldNames] = React.useState([]);

  const [showNoValueFields, setShowNoValueFields] = React.useState(false);
  const [showNoEditFields] = React.useState(true);
  const [hide, setHide] = React.useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    const success = await onSave(id, fieldData);
    await sleep(2000);
    setIsSaving(false);
    if (success) {
      showSuccessBanner('RFP item changes saved successfully', null, dispatch);
    } else {
      showErrorBanner(
        'Could not save RFP item changes, document may still be processing previous edits. Please wait a moment, then try again',
        null,
        dispatch
      );
    }
  };

  const handleConfirmDelete = async () => {
    const URL = `v1/genai/rfp_responses/${id}`;
    const { status, data, error } = await sendApiRequest('delete', URL);
    logNetRequest(status, data);
    if (status === 200) {
      setHide(true);
    } else {
      showErrorBanner('Could not delete RFP, please try again later', null, dispatch);
    }
  };

  const { ConfirmationModal, onShowModal } = useConfirmationModal(handleConfirmDelete);

  React.useEffect(() => {
    if (fields) {
      const nonMainFieldNames = fields.filter(
        (field) => !(mainFieldNames.includes(field.name) || noUseFieldNames.includes(field.name))
      );
      const editableFields = nonMainFieldNames.filter((field) => !isCannotUpdateName(field.name));
      const filteredFields = editableFields.filter((field) => !isBlank(field.value));

      const cannotUpdateFields = nonMainFieldNames.filter((field) => isCannotUpdateName(field.name));

      setFieldData(fields);
      setDisplayFieldNames(filteredFields.map((field) => field.name));
      setCannotUpdateFieldNames(cannotUpdateFields.map((field) => field.name));
    }
  }, []);

  React.useEffect(() => {
    const nonMainFieldNames = fields.filter(
      (field) => !(mainFieldNames.includes(field.name) || noUseFieldNames.includes(field.name))
    );

    if (showNoValueFields) {
      const editableFields = nonMainFieldNames.filter((field) => !isCannotUpdateName(field.name));
      setDisplayFieldNames(editableFields.map((field) => field.name));
    } else {
      const editableFields = nonMainFieldNames.filter((field) => !isCannotUpdateName(field.name));
      const filteredFields = editableFields.filter((field) => !isBlank(field.value));
      setDisplayFieldNames(filteredFields.map((field) => field.name));
    }
  }, [showNoValueFields, fields]);

  const onToggle = () => {
    setIsOpen(!isOpen);
  };

  const onFieldChange = (name, value) => {
    setFieldData((prev) => updateFieldData(prev, name, value));
  };

  const confirmDelete = () => {
    const displayName = filename || `RFP ${id}`;

    const modalBody = (
      <Container centerAll>
        <Body5 center>Are you sure you wish to delete {displayName}?</Body5>
      </Container>
    );
    onShowModal(modalBody, data);
  };

  if (hide) {
    return (
      <RFPItemWrapper>
        <RFPItemDescription>
          <Container>
            {filename ? null : <Body5>Id: {id}</Body5>}
            <Body5 bold>{filename || ' - No filename'}</Body5>
          </Container>
          <Container left={16}>
            <Body5 bold>Deleted</Body5>
          </Container>
        </RFPItemDescription>
      </RFPItemWrapper>
    );
  }

  return (
    <RFPItemWrapper className={!isOpen ? 'is-collapsed' : ''}>
      {ConfirmationModal}
      <RFPItemDescription onClick={isOpen ? () => null : onToggle}>
        <Container>
          {filename ? null : <Body5>Id: {id}</Body5>}
          <Body5 bold>{filename || ' - No filename'}</Body5>
        </Container>
        <Container top={8}>
          <CollapseButton isCollapsed={!isOpen} setCollapsed={(v) => setIsOpen(!v)} />
        </Container>
      </RFPItemDescription>
      {isOpen ? (
        <RFPItemBody>
          {null && (
            <Container bottom={24}>
              <Body5>{JSON.stringify(doc_metadata)}</Body5>
            </Container>
          )}

          {!fieldData ? (
            <Container top={24}>
              <Body5>No fields found</Body5>
            </Container>
          ) : (
            <ShowFieldsActionsWrapper>
              {showNoValueFields ? (
                <TransparentFlatButton onClick={() => setShowNoValueFields(false)}>
                  Hide Empty Fields
                </TransparentFlatButton>
              ) : (
                <TransparentFlatButton onClick={() => setShowNoValueFields(true)}>
                  Show Empty Fields
                </TransparentFlatButton>
              )}
              <Container right={8}>
                <TransparentFlatButton onClick={confirmDelete}>Delete RFP Item</TransparentFlatButton>
              </Container>
            </ShowFieldsActionsWrapper>
          )}

          <Container top={36}>
            {mainDisplayFieldNames?.map((name) => (
              <Field
                key={name}
                data={getFieldData(name, fieldData)}
                onFieldChange={onFieldChange}
                onSave={handleSave}
              />
            ))}
            {displayFieldNames?.map((name) => (
              <Field
                key={name}
                data={getFieldData(name, fieldData)}
                onFieldChange={onFieldChange}
                onSave={handleSave}
              />
            ))}
          </Container>
          <Container top={24} style={{ textAlign: 'center' }}>
            <SaveButton isSaving={isSaving} onClick={handleSave} />
          </Container>

          {cannotUpdateFieldNames.length > 0 && showNoEditFields && (
            <Container top={24}>
              <Container bottom={24}>
                <Body5>Read only fields:</Body5>
              </Container>
              {cannotUpdateFieldNames.map((fieldName) => (
                <NoEditField key={fieldName} data={getFieldData(fieldName, fieldData)} />
              ))}
            </Container>
          )}
        </RFPItemBody>
      ) : null}
    </RFPItemWrapper>
  );
};

function RFPEditTool({ rfpResponses }) {
  const onSave = async (id, data) => {
    const URL = `v1/genai/rfp_responses/${id}`;
    const { status, error } = await sendApiRequest('PATCH', URL, { rfp_response: { fields: data } });
    logNetRequest(status, data);
    if (status === 200) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <Wrapper>
      {rfpResponses?.map((rfpResponse) => (
        <RFPItem key={rfpResponse.id} data={rfpResponse} onSave={onSave} />
      ))}
    </Wrapper>
  );
}

export default RFPEditTool;
